/**
 * Client-side mail-merge for the campaign wizard's Review preview.
 *
 * MUST stay in sync with the backend send-time rendering in
 * supabase/functions/campaign-scheduler/index.ts (the `variableMap` plus the
 * case-insensitive `{{ var }}` regex). The wizard now previews recipients
 * client-side before the campaign is created, so if this diverges from the
 * backend the user sees one thing and recipients receive another. Change both
 * together.
 *
 * Note: this intentionally does NOT use the Handlebars-based `renderTemplate`
 * in template-utils.ts — Handlebars is case-sensitive and drops unknown tokens,
 * whereas the backend is case-insensitive and leaves unmapped tokens untouched.
 */

export interface CampaignMergeRecipient {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
  occupation?: string | null;
  phone?: string | null;
}

/**
 * Canonical set of merge-variable names the backend will substitute at send
 * time. Stored lowercase because send-time matching is case-insensitive
 * (see the `gi` flag on the substitution regex below). Imported by the
 * template editor's pre-save validator to detect unknown variables and
 * single-brace typos before a campaign is saved with broken merges.
 *
 * If a new variable is added here, also add the corresponding read in
 * variableMap below AND in the backend send-time renderer at
 * supabase/functions/campaign-scheduler/index.ts (search for `variableMap`).
 */
export const KNOWN_VARIABLE_NAMES: ReadonlySet<string> = new Set([
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'company',
  'companyname',
  'email',
  'occupation',
  'phone',
]);

export function renderCampaignTemplate(
  template: string,
  recipient: CampaignMergeRecipient,
): string {
  const variableMap: Record<string, string> = {
    first_name: recipient.first_name || '',
    firstname: recipient.first_name || '',
    last_name: recipient.last_name || '',
    lastname: recipient.last_name || '',
    company: recipient.company || '',
    companyname: recipient.company || '',
    email: recipient.email || '',
    occupation: recipient.occupation || '',
    phone: recipient.phone || '',
  };

  let rendered = template;
  for (const [varName, value] of Object.entries(variableMap)) {
    const regex = new RegExp(`\\{\\{\\s*${varName}\\s*\\}\\}`, 'gi');
    rendered = rendered.replace(regex, value);
  }
  return rendered;
}
